import React, { useEffect, useState, useContext, useRef, useCallback, useMemo } from "react";
import { callFunction } from "../api/callFunction";
import EventAPI from "../api/EventApi";
import EventAttendanceApi from "../api/EventAttendanceApi";
import { GlobalContext } from "../context/GlobalContext";
import { useUserSelection } from "../hooks/useUserSelection";
import {
  UserMasonryGallery,
  UserSelectionConfirmModal,
  EnlargedPhotoModal,
} from "../components/UserGallery";
import RsvpList from "./RsvpList";
import PopularGuestsSection from "./PopularGuestsSection";
import HostPopularGuests from "./HostPopularGuests";
import { readExplainerSeen } from "./PopularExplainer";
import { popularMode } from "./popularHostRules";
import NewBadge from "../components/NewBadge";
import { showToast } from "../utils/showToast";
import { variantURL, fallbackToOriginal } from "../utils/imageVariant";
import { eventDetailsBareUrl } from "../utils/links";
import { fetchHostPastEvents, guestsFromEvents } from "./utils/pastEventGuests";
import { hostsFirst } from "./utils/eventHosts";
import { attendanceOutcomeText, attendanceSummary, attendanceSummaryText, isReleasedNo } from "./utils/attendance";

const USERS_PER_PAGE = 20;
const MAX_INVITE_LIMIT = 50;
const MAX_MESSAGE_LENGTH = 300; // matches MAX_CUSTOM_MESSAGE_LENGTH in sendEventInvite

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

// "2026-05-03" -> "May 2026" (string-sliced to dodge timezone off-by-one).
function formatMonth(dateStr) {
  if (typeof dateStr !== "string") return "";
  const [y, m] = dateStr.split("-");
  const idx = Number(m) - 1;
  return MONTHS[idx] ? `${MONTHS[idx]} ${y}` : "";
}

// One-line "why this person" label shown on recommended-guest cards.
function buildReasonLabel(reasons = {}) {
  const parts = [];
  if (reasons.type && reasons.type !== "mixed") parts.push(reasons.type);
  if (reasons.similarEvents) parts.push(`${reasons.similarEvents} similar`);
  if (reasons.nearbyEvents) parts.push(`${reasons.nearbyEvents} nearby`);
  // From "Can you make it?": how often they show up after saying yes.
  if (reasons.reliability?.saidYes) parts.push(`showed up ${reasons.reliability.showed} of ${reasons.reliability.saidYes}`);
  const month = formatMonth(reasons.lastAttended);
  if (month) parts.push(month);
  return parts.join(" · ");
}

// Sits where the invite grid goes, so the tabs above it stay on screen and
// clickable while the pool loads.
function UserPoolSpinner() {
  return (
    <div className="flex items-center justify-center py-12">
      <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400">
        <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
            fill="none"
          />
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          />
        </svg>
        <span>Loading users...</span>
      </div>
    </div>
  );
}

export function InviteSection({ eventID, eventData }) {
  const { globalState } = useContext(GlobalContext);
  const [allUsers, setAllUsers] = useState([]);
  const [visibleCount, setVisibleCount] = useState(USERS_PER_PAGE);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [enlargedPhotoUser, setEnlargedPhotoUser] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showInvitedSection, setShowInvitedSection] = useState(false);
  const [userSource, setUserSource] = useState("all"); // "all" | "pastEvents" | "recommended" (vendors get "all" = their past guests, + "recommended")
  const [activeTab, setActiveTab] = useState("guests");
  const [recommendedUsers, setRecommendedUsers] = useState([]);
  const [recMeta, setRecMeta] = useState(null);
  const [recError, setRecError] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  // The invite pool is the expensive fetch on this section — for a super admin it
  // pages the entire user directory — and neither Guests nor Popular reads a line
  // of it. Latch on the first visit to Invites so the fetch runs once, on demand,
  // and survives every later tab switch.
  const [invitesOpened, setInvitesOpened] = useState(false);
  useEffect(() => {
    if (activeTab === "invites") setInvitesOpened(true);
  }, [activeTab]);

  // "admin", "host" or null: which Popular tab, if any (see popularMode).
  const popular = popularMode(globalState);
  // A host who has not dismissed the Popular explainer yet gets the "new" mark
  // on the tab. Read once; the explainer tells us when it is dismissed.
  const [explainerSeen, setExplainerSeen] = useState(readExplainerSeen);
  // The warm start draws from the cached vendor doc and the fresh read can take
  // the tab away (a revoked grant). Fall back rather than show an empty section.
  useEffect(() => {
    if (!popular && activeTab === "popular") setActiveTab("guests");
  }, [popular, activeTab]);

  // "Can you make it?" replies, live: the backend texts every RSVP the day
  // before and answers land here as they come in. Empty until the question
  // has gone out (or with the feature off), so the guest list looks as before.
  const [attendanceByUser, setAttendanceByUser] = useState({});
  useEffect(() => {
    if (!eventID) return undefined;
    setAttendanceByUser({});
    const unsubscribe = EventAttendanceApi.subscribeByEvent(
      eventID,
      setAttendanceByUser,
      (err) => console.error("[InviteSection] eventAttendance subscription error:", err)
    );
    return unsubscribe;
  }, [eventID]);
  // After the event the backend rolls the answers up against the check-in list
  // (eventAttendanceSummaries/{eventId}.outcome): who said yes and showed.
  const [attendanceOutcome, setAttendanceOutcome] = useState(null);
  useEffect(() => {
    if (!eventID) return undefined;
    setAttendanceOutcome(null);
    const unsubscribe = EventAttendanceApi.subscribeSummary(
      eventID,
      (summary) => setAttendanceOutcome(summary?.outcome || null),
      (err) => console.error("[InviteSection] eventAttendanceSummaries subscription error:", err)
    );
    return unsubscribe;
  }, [eventID]);
  const attendanceOutcomeLine = useMemo(() => attendanceOutcomeText(attendanceOutcome), [attendanceOutcome]);

  const loadMoreRef = useRef(null);
  const scrollContainerRef = useRef(null);

  const {
    selectedUsers,
    isSelectionMode,
    handleToggleUserSelection,
    isUserSelected,
    clearSelection,
    selectAll,
    setIsSelectionMode,
  } = useUserSelection();

  const rsvpUsers = useMemo(() => {
    if (!eventData?.eventUsers) return [];
    const invitedSitters = eventData.invitedSitters || [];
    const rsvpList = [
      ...(eventData.rsvpMovers || []).map(userId => ({
        userID: userId,
        name: eventData.eventUsers[userId]?.name,
        status: "rsvped",
        type: "movers",
        photoURL: eventData.eventUsers[userId]?.photoURL,
        approved: eventData.eventUsers[userId]?.approved,
        invited: invitedSitters.includes(userId),
        attendance: attendanceByUser[userId] || null,
      })),
      ...(eventData.rsvpSitters || []).map(userId => ({
        userID: userId,
        name: eventData.eventUsers[userId]?.name,
        status: "rsvped",
        type: "sitters",
        photoURL: eventData.eventUsers[userId]?.photoURL,
        approved: eventData.eventUsers[userId]?.approved,
        invited: invitedSitters.includes(userId),
        attendance: attendanceByUser[userId] || null,
      })),
      ...(eventData.movers || []).map(userId => ({
        userID: userId,
        name: eventData.eventUsers[userId]?.name,
        status: "checked-in",
        type: "movers",
        photoURL: eventData.eventUsers[userId]?.photoURL,
        approved: eventData.eventUsers[userId]?.approved,
        checkInUnverified: eventData.eventUsers[userId]?.checkInUnverified,
        checkInGatePhase: eventData.eventUsers[userId]?.checkInGatePhase,
        invited: invitedSitters.includes(userId),
        attendance: attendanceByUser[userId] || null,
      })),
      ...(eventData.sitters || []).map(userId => ({
        userID: userId,
        name: eventData.eventUsers[userId]?.name,
        status: "checked-in",
        type: "sitters",
        photoURL: eventData.eventUsers[userId]?.photoURL,
        approved: eventData.eventUsers[userId]?.approved,
        checkInUnverified: eventData.eventUsers[userId]?.checkInUnverified,
        checkInGatePhase: eventData.eventUsers[userId]?.checkInGatePhase,
        invited: invitedSitters.includes(userId),
        attendance: attendanceByUser[userId] || null,
      }))
    ];
    // The host and co-hosts sit at the top, so the host can find them.
    return hostsFirst(eventData, rsvpList.filter(user => user.name || user.photoURL));
  }, [eventData, attendanceByUser]);

  // Counted from the guests ON the list, not from every answer on file: someone
  // who said yes and then cancelled still has a doc, and a host reading this
  // line as a headcount would be one over for each of them.
  //
  // "On the list" is the event's four guest arrays, NOT rsvpUsers: that one is
  // the display list, which drops a guest whose snapshot has no name or photo,
  // and a guest we cannot draw still said yes and is still coming.
  //
  // The one exception is a guest whose NO gave their spot back ("A no gives the
  // spot back" switch): they left the list BECAUSE they answered, and leaving
  // them out would hide every cancellation. They only ever add to "can't make it".
  const attendanceSummaryLine = useMemo(() => {
    const onList = new Set([
      ...(eventData?.rsvpSitters || []),
      ...(eventData?.rsvpMovers || []),
      ...(eventData?.sitters || []),
      ...(eventData?.movers || []),
    ]);
    const counted = {};
    Object.entries(attendanceByUser).forEach(([userId, entry]) => {
      if (onList.has(userId) || isReleasedNo(entry)) counted[userId] = entry;
    });
    const summary = attendanceSummary(counted);
    return summary.total > 0 ? attendanceSummaryText(summary) : null;
  }, [eventData, attendanceByUser]);

  const guestCount =
    (eventData?.rsvpSitters?.length || 0) +
    (eventData?.sitters?.length || 0) +
    (eventData?.rsvpMovers?.length || 0) +
    (eventData?.movers?.length || 0);

  // Keyed off the individual fields, not the eventData object: EventForm hands us a new
  // eventData identity on every keystroke, and re-filtering the whole users collection
  // that often is real work.
  const { vendorID, coHostIDs, rsvpSitters, rsvpMovers, sitters, movers, invitedSitters, eventUsers } = eventData || {};

  // Anyone already attached to this event — the host and co-hosts, RSVPs, check-ins,
  // and people invited earlier — can't be invited again, so they never belong in the pool. The
  // All Users / Past Events / Past Guests sources are queried client-side and so are
  // filtered here; the Recommended source is already filtered by getRecommendedGuests.
  // Deliberately keyed off the arrays and not eventUsers: cancelling an RSVP leaves the
  // eventUsers entry behind, and someone who cancelled is invitable again. This mirrors
  // eventAttendeeIds() on the server, so the grid and sendEventInvite agree on who is
  // invitable.
  const excludedUserIDs = useMemo(() => new Set([
    vendorID,
    ...(Array.isArray(coHostIDs) ? coHostIDs : []),
    ...(rsvpSitters || []),
    ...(rsvpMovers || []),
    ...(sitters || []),
    ...(movers || []),
    ...(invitedSitters || []),
  ].filter(Boolean)), [vendorID, coHostIDs, rsvpSitters, rsvpMovers, sitters, movers, invitedSitters]);

  const availableUsers = useMemo(
    () => allUsers.filter((user) => !excludedUserIDs.has(user.id)),
    [allUsers, excludedUserIDs]
  );

  const displayedUsers = useMemo(
    () => availableUsers.slice(0, visibleCount),
    [availableUsers, visibleCount]
  );
  const hasMore = visibleCount < availableUsers.length;

  // Invited users are filtered out of the pool above, so read their cards off the event
  // doc (sendEventInvite seeds name/photoURL into eventUsers), falling back to the
  // fetched pool for anyone whose eventUsers entry was dropped when a host removed them.
  const invitedUsers = useMemo(() => (invitedSitters || []).map((userID) => {
    const snapshot = eventUsers?.[userID] || allUsers.find((user) => user.id === userID) || {};
    return { id: userID, name: snapshot.name || "", photoURL: snapshot.photoURL || "" };
  }), [invitedSitters, eventUsers, allUsers]);

  // Someone can join the event (or be invited from another session) while they sit
  // selected here. Their card vanishes from the grid, so drop them from the pending
  // batch too — otherwise they stay in the count and the confirm modal with no card to
  // deselect, and the send just reports them back as skipped.
  useEffect(() => {
    const stillInvitable = selectedUsers.filter((user) => !excludedUserIDs.has(user.id));
    if (stillInvitable.length !== selectedUsers.length) selectAll(stillInvitable);
  }, [excludedUserIDs, selectedUsers, selectAll]);

  const handleRemoveRsvpUser = async (userID, userType) => {
    if (!eventID) return;

    // Atomic per-field removal (see EventAPI.removeGuest): this races live
    // RSVP/check-in writes, so it never rewrites arrays from the held eventData.
    try {
      await EventAPI.removeGuest(eventID, userID, userType);
      showToast("User removed from event", { type: "success" });
    } catch (error) {
      console.error("Error removing user:", error);
      showToast("Failed to remove user", { type: "error" });
    }
  };

  // Fetch users for invitation
  useEffect(() => {
    if (!invitesOpened) return;
    const fetchUsers = async () => {
      // Recommended source is populated by the generate button, not a fetch;
      // switching back to it just restores the last generated batch.
      if (userSource === "recommended") {
        setAllUsers(recommendedUsers);
        setVisibleCount(USERS_PER_PAGE);
        setIsLoading(false);
        return;
      }
      setIsLoading(true);
      setVisibleCount(USERS_PER_PAGE);
      try {
        // Super admin with "all" source: fetch all users from users collection
        if (globalState.isSuperAdmin && userSource === "all") {
          // Server-projected name/photo directory, paged — not the whole users
          // collection with everyone's phone/email/device token (GIS-184). The
          // invite gallery only ever reads id/name/photoURL.
          const users = [];
          let cursor = null;
          do {
            const res = await callFunction("listUserDirectory", { cursor, pageSize: 1000 }, { timeout: 60000 });
            (res?.users || []).forEach((u) =>
              users.push({ id: u.userID, name: u.name, photoURL: u.photoURL, vendorID: u.vendorID })
            );
            cursor = res?.nextCursor || null;
          } while (cursor);
          setAllUsers(users);
        } else {
          // Non-super admin OR super admin with "pastEvents" source: guests from
          // the events this host hosted, co-hosted (GIS-285) or attended.
          const pastEvents = await fetchHostPastEvents(globalState.vendorID, { includeAttended: true });
          setAllUsers(guestsFromEvents(pastEvents));
        }
      } catch (error) {
        console.error("Error fetching users:", error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchUsers();
  }, [globalState.isSuperAdmin, globalState.vendorID, userSource, recommendedUsers, invitesOpened]);

  // Load more users with scroll position preservation
  const loadMoreUsers = useCallback(() => {
    if (isLoadingMore || !hasMore) return;

    // Save current scroll position before loading
    const scrollTop = scrollContainerRef.current?.scrollTop || 0;

    setIsLoadingMore(true);
    setVisibleCount((count) => count + USERS_PER_PAGE);

    // Restore scroll position after DOM update
    requestAnimationFrame(() => {
      if (scrollContainerRef.current) {
        scrollContainerRef.current.scrollTop = scrollTop;
      }
      setIsLoadingMore(false);
    });
  }, [hasMore, isLoadingMore]);

  // Intersection Observer for infinite scroll
  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    const currentRef = loadMoreRef.current;
    if (!currentRef || !scrollContainer) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !isLoadingMore) {
          loadMoreUsers();
        }
      },
      { threshold: 0.1, rootMargin: "200px", root: scrollContainer }
    );

    observer.observe(currentRef);

    return () => {
      observer.unobserve(currentRef);
    };
  }, [hasMore, isLoadingMore, loadMoreUsers, activeTab]);

  // Auto-enable selection mode when component mounts
  useEffect(() => {
    setIsSelectionMode(true);
  }, [setIsSelectionMode]);

  // Auto-close confirm modal when all users are removed
  useEffect(() => {
    if (showConfirmModal && selectedUsers.length === 0) {
      setShowConfirmModal(false);
    }
  }, [showConfirmModal, selectedUsers.length]);

  // Clamped: the 50-cap is enforced here, not on the server, so an event can already
  // hold more than 50 invites. A negative remainder would block every card and render
  // "you can invite -7 more".
  const remainingInviteSlots = Math.max(0, MAX_INVITE_LIMIT - invitedUsers.length);

  const handleCardClick = (user) => {
    if (isSelectionMode) {
      if (!isUserSelected(user.id) && selectedUsers.length >= remainingInviteSlots) return;
      handleToggleUserSelection(user);
    } else {
      setEnlargedPhotoUser(user);
    }
  };

  const handleCloseEnlargedPhoto = () => {
    setEnlargedPhotoUser(null);
  };

  const handleGenerateRecommendations = async () => {
    setIsGenerating(true);
    setRecError(null);
    try {
      const result = await callFunction("getRecommendedGuests", { eventID }, { timeout: 60000 });
      const recs = (result?.recommendations || []).map((rec) => ({
        id: rec.id,
        name: rec.name,
        photoURL: rec.photoURL,
        subtitle: buildReasonLabel(rec.reasons),
        tooltip: rec.reasons?.topEvents?.length
          ? `Attended: ${rec.reasons.topEvents.join(", ")}`
          : undefined,
      }));
      setRecommendedUsers(recs);
      setRecMeta(result?.meta || null);
    } catch (error) {
      console.error("Failed to generate recommendations:", error);
      setRecError(error.message || "Failed to generate recommendations");
    } finally {
      setIsGenerating(false);
    }
  };

  const [isSending, setIsSending] = useState(false);
  const [inviteResult, setInviteResult] = useState(null);
  const [customMessage, setCustomMessage] = useState("");

  const trimmedMessage = customMessage.trim();

  const handleConfirmInvite = async () => {
    setIsSending(true);
    setInviteResult(null);

    try {
      const result = await callFunction("sendEventInvite", {
        eventID,
        userIDs: selectedUsers.map((u) => u.id),
        // Omitted entirely when blank, so the server sends its stock invite copy.
        ...(trimmedMessage ? { message: trimmedMessage } : {}),
      });

      const { sent, skipped, invalid = 0, smsResults, pushResults } = result;

      // `skipped` covers everyone not sent to; `invalid` is the deleted-account slice of
      // it. Name them separately so the numbers add up for whoever is reading.
      const notes = [];
      if (skipped - invalid > 0) notes.push(`${skipped - invalid} already invited or attending`);
      if (invalid > 0) notes.push(`${invalid} no longer have an account`);

      setInviteResult({
        success: true,
        message: `Invites sent to ${sent} user${sent !== 1 ? "s" : ""}${notes.length ? ` (${notes.join(", ")})` : ""}`,
        details: `SMS: ${smsResults?.successful || 0}, Push: ${pushResults?.successful || 0}`,
      });

      clearSelection();
      setIsSelectionMode(true);
      setCustomMessage("");
    } catch (error) {
      console.error("Failed to send invites:", error);
      setInviteResult({
        success: false,
        message: error.message || "Failed to send invites. Please try again.",
      });
    } finally {
      setIsSending(false);
      setShowConfirmModal(false);
    }
  };

  return (
    <div>
      <div className="flex gap-1 bg-gray-100 dark:bg-gray-800 p-1 rounded-lg mb-4">
        <button
          onClick={() => setActiveTab("guests")}
          className={`flex-1 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
            activeTab === "guests"
              ? "bg-white dark:bg-gray-800 shadow text-gray-900 dark:text-gray-100"
              : "text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-200"
          }`}
        >
          Guests ({guestCount})
        </button>
        <button
          onClick={() => setActiveTab("invites")}
          className={`flex-1 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
            activeTab === "invites"
              ? "bg-white dark:bg-gray-800 shadow text-gray-900 dark:text-gray-100"
              : "text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-200"
          }`}
        >
          Invites
          <NewBadge />
        </button>
        {/* Popular is a platform-wide ranking. Operators get the full tab: its
            callables use the same claim-or-flag gate as listUserDirectory, so
            gating on isSuperAdmin (not the bare claim) is right here. A host an
            operator has given access to (vendors/{uid}.popularGuests) gets the
            ranking and Reach out on their own events, nothing about money. The
            server checks both; this only decides what to draw. */}
        {popular && (
          <button
            onClick={() => setActiveTab("popular")}
            className={`flex-1 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
              activeTab === "popular"
                ? "bg-white dark:bg-gray-800 shadow text-gray-900 dark:text-gray-100"
                : "text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-200"
            }`}
          >
            Popular
            {(popular === "admin" || !explainerSeen) && <NewBadge />}
          </button>
        )}
      </div>

      {activeTab === "guests" && (
        <>
          {attendanceSummaryLine && (
            <p className="mb-3 text-center text-sm text-gray-600 dark:text-gray-300">
              <span className="font-medium text-gray-900 dark:text-gray-100">Can you make it?</span>{" "}
              {attendanceSummaryLine}
            </p>
          )}
          {attendanceOutcomeLine && (
            <p className="mb-3 text-center text-sm text-gray-600 dark:text-gray-300">{attendanceOutcomeLine}</p>
          )}
          <RsvpList rsvpUsers={rsvpUsers} event={eventData} onRemove={handleRemoveRsvpUser} alwaysExpanded />
        </>
      )}

      {activeTab === "popular" && popular === "admin" && (
        <PopularGuestsSection eventID={eventID} eventData={eventData} />
      )}
      {activeTab === "popular" && popular === "host" && (
        <HostPopularGuests
          eventID={eventID}
          eventData={eventData}
          onExplainerSeen={() => setExplainerSeen(true)}
        />
      )}

      {activeTab === "invites" && (<>
      {inviteResult && (
        <div
          className={`mb-4 p-3 rounded-md ${inviteResult.success
            ? "bg-green-50 dark:bg-green-900/40 border border-green-200 dark:border-green-800 text-green-800 dark:text-green-300"
            : "bg-red-50 dark:bg-red-900/40 border border-red-200 dark:border-red-800 text-red-800 dark:text-red-300"
            }`}
        >
          <p className="font-medium">{inviteResult.message}</p>
          {inviteResult.details && (
            <p className="text-sm mt-1 opacity-75">{inviteResult.details}</p>
          )}
          <button
            onClick={() => setInviteResult(null)}
            className="text-sm underline mt-1"
          >
            Dismiss
          </button>
        </div>
      )}

      {!isLoading && (userSource !== "recommended" || availableUsers.length > 0) && (
        <p className="text-gray-600 dark:text-gray-300 text-sm mb-2">
          {availableUsers.length} users available
        </p>
      )}

      {invitedUsers.length > 0 && (
        <div className="mb-4 p-4 bg-slate-50 dark:bg-slate-900 border border-stone-300 dark:border-stone-600 rounded-md">
          <button
            type="button"
            onClick={() => setShowInvitedSection(!showInvitedSection)}
            className="w-full flex items-center justify-between text-left focus:outline-none"
          >
            <label className="text-md font-medium text-gray-500 dark:text-gray-400">
              Invited Users ({invitedUsers.length})
            </label>
            <svg
              className={`w-5 h-5 transform transition-transform ${showInvitedSection ? "rotate-180" : ""}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          {showInvitedSection && (
            <div className="mt-4">
              <div className="flex flex-wrap gap-y-4 justify-center">
                {invitedUsers.map((user) => (
                  <div key={user.id} className="flex flex-col items-center text-center w-20">
                    <div
                      className="relative cursor-pointer"
                      onClick={() => setEnlargedPhotoUser(user)}
                    >
                      {user.photoURL ? (
                        <img
                          src={variantURL(user.photoURL, 200)}
                          onError={fallbackToOriginal(user.photoURL)}
                          alt={user.name}
                          className="w-16 h-16 rounded-full object-cover"
                        />
                      ) : (
                        <div className="w-16 h-16 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center">
                          <span className="text-gray-500 dark:text-gray-400 text-2xl">
                            {user.name && user.name.length > 0 ? user.name.charAt(0).toUpperCase() : "?"}
                          </span>
                        </div>
                      )}
                      <div className="absolute top-0 right-0 bg-primary rounded-full p-1">
                        <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                          <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                        </svg>
                      </div>
                    </div>
                    <div className="font-light text-gray-900 dark:text-gray-100 mt-2 text-xs overflow-hidden line-clamp-2">
                      {user.name}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      <div className="flex gap-1 bg-gray-100 dark:bg-gray-800 p-1 rounded-lg mb-3">
        {globalState.isSuperAdmin ? (
          <>
            <button
              onClick={() => setUserSource("all")}
              className={`flex-1 px-3 py-2 rounded-md text-sm font-medium transition-colors ${userSource === "all"
                ? "bg-white dark:bg-gray-800 shadow text-gray-900 dark:text-gray-100"
                : "text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-200"
                }`}
            >
              All Users
            </button>
            <button
              onClick={() => setUserSource("pastEvents")}
              className={`flex-1 px-3 py-2 rounded-md text-sm font-medium transition-colors ${userSource === "pastEvents"
                ? "bg-white dark:bg-gray-800 shadow text-gray-900 dark:text-gray-100"
                : "text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-200"
                }`}
            >
              Past Events
            </button>
          </>
        ) : (
          <button
            onClick={() => setUserSource("all")}
            className={`flex-1 px-3 py-2 rounded-md text-sm font-medium transition-colors ${userSource !== "recommended"
              ? "bg-white dark:bg-gray-800 shadow text-gray-900 dark:text-gray-100"
              : "text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-200"
              }`}
          >
            Past Guests
          </button>
        )}
        <button
          onClick={() => setUserSource("recommended")}
          className={`flex-1 px-3 py-2 rounded-md text-sm font-medium transition-colors ${userSource === "recommended"
            ? "bg-white dark:bg-gray-800 shadow text-gray-900 dark:text-gray-100"
            : "text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-200"
            }`}
        >
          Recommended
          <NewBadge />
        </button>
      </div>

      {userSource === "recommended" && (
        <div className="mb-4">
          {recError && (
            <div className="mb-3 p-3 rounded-md bg-red-50 dark:bg-red-900/40 border border-red-200 dark:border-red-800 text-red-800 dark:text-red-300 text-sm">
              {recError}
            </div>
          )}
          {recommendedUsers.length === 0 ? (
            <div className="p-6 border border-dashed border-gray-300 dark:border-gray-600 rounded-md text-center">
              <p className="text-sm text-gray-600 dark:text-gray-300 mb-3">
                {recMeta
                  ? globalState.isSuperAdmin
                    ? "No recommendations found for this event. Try the All Users source instead."
                    : "No recommendations found for this event yet."
                  : "Find members who attended past events near this venue or with a similar theme."}
              </p>
              <button
                onClick={handleGenerateRecommendations}
                disabled={isGenerating}
                className={`px-4 py-2 rounded-md font-medium transition-colors ${isGenerating
                  ? "bg-gray-400 text-white cursor-not-allowed"
                  : "bg-primary text-white hover:bg-primaryDark"
                  }`}
              >
                {isGenerating ? "Generating..." : "Generate recommended guests"}
              </button>
            </div>
          ) : (
            <div className="flex items-center justify-between text-sm text-gray-500 dark:text-gray-400">
              <span>
                Ranked by attendance at {recMeta?.candidateEvents ?? 0} similar or nearby past events
              </span>
              <button
                onClick={handleGenerateRecommendations}
                disabled={isGenerating}
                className="underline hover:text-gray-700 dark:hover:text-gray-200"
              >
                {isGenerating ? "Refreshing..." : "Refresh"}
              </button>
            </div>
          )}
        </div>
      )}

      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-4">
        {
          selectedUsers.length > 0 && (
            <div className="flex gap-2">
              <button
                onClick={() => setShowConfirmModal(true)}
                disabled={isSending}
                className={`px-4 py-2 rounded-md font-medium transition-colors ${isSending
                  ? "bg-gray-400 text-white cursor-not-allowed"
                  : "bg-primary text-white hover:bg-primaryDark"
                  }`}
              >
                {isSending ? "Sending..." : `Review Invites (${selectedUsers.length}/${remainingInviteSlots})`}
              </button>
              {isSelectionMode && !isSending && (
                <button
                  onClick={() => { clearSelection(); setIsSelectionMode(true); }}
                  className="px-4 py-2 rounded-md font-medium bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 transition-colors hover:bg-gray-300 dark:hover:bg-gray-600"
                >
                  Clear
                </button>
              )}
            </div>
          )
        }
        {selectedUsers.length >= remainingInviteSlots && (
          <p className="text-sm text-amber-600 dark:text-amber-400">
            {invitedUsers.length > 0
              ? `${invitedUsers.length} already invited. You can invite ${remainingInviteSlots} more (max ${MAX_INVITE_LIMIT} per event).`
              : `You can invite a maximum of ${MAX_INVITE_LIMIT} people per event.`}
          </p>
        )}
      </div>

      {/* A small host can have every past guest already on this event. Say so, rather
          than leaving a blank grid. Recommended has its own empty state above. */}
      {isLoading ? (
        <UserPoolSpinner />
      ) : userSource !== "recommended" && availableUsers.length === 0 ? (
        <div className="p-6 border border-dashed border-gray-300 dark:border-gray-600 rounded-md text-center">
          <p className="text-sm text-gray-600 dark:text-gray-300">
            Everyone in this list is already invited or attending. Try Recommended to find new guests.
          </p>
        </div>
      ) : (
        <div ref={scrollContainerRef} className="max-h-[calc(100dvh-300px)] overflow-y-auto">
          <UserMasonryGallery
            users={displayedUsers}
            isSelectionMode={isSelectionMode}
            isUserSelected={isUserSelected}
            onUserClick={handleCardClick}
            onExpandPhoto={setEnlargedPhotoUser}
            loadMoreRef={loadMoreRef}
            isLoadingMore={isLoadingMore}
            hasMore={hasMore}
            totalCount={availableUsers.length}
            compact
          />
        </div>
      )}

      <UserSelectionConfirmModal
        isOpen={showConfirmModal}
        selectedUsers={selectedUsers}
        onConfirm={handleConfirmInvite}
        onCancel={() => setShowConfirmModal(false)}
        onRemoveUser={handleToggleUserSelection}
        title="Confirm Event Invite"
        confirmText={isSending ? "Sending..." : "Send Invites"}
        isLoading={isSending}
        description={`You are about to invite ${selectedUsers.length} user${selectedUsers.length > 1 ? "s" : ""
          } to ${eventData?.name || "this event"}:`}
      >
        <label htmlFor="inviteMessage" className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
          Message (optional)
        </label>
        <textarea
          id="inviteMessage"
          rows={3}
          value={customMessage}
          onChange={(e) => setCustomMessage(e.target.value.slice(0, MAX_MESSAGE_LENGTH))}
          disabled={isSending}
          placeholder={`You're invited to ${eventData?.name || "an event"} on Gist!`}
          className="w-full px-3 py-2 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 text-sm focus:outline-none focus:ring-1 focus:ring-primary disabled:opacity-50"
        />
        <div className="flex justify-between items-center mt-1 text-xs text-gray-500 dark:text-gray-400">
          <span>Leave blank to send the standard invite. The RSVP link is added for you.</span>
          <span>{customMessage.length}/{MAX_MESSAGE_LENGTH}</span>
        </div>
        {trimmedMessage && (
          <div className="mt-3">
            <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">They will get:</p>
            <p className="p-3 rounded-md bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 text-sm text-gray-800 dark:text-gray-200 whitespace-pre-line break-words">
              {`${trimmedMessage}\n\nRSVP at ${eventDetailsBareUrl(eventID)}`}
            </p>
          </div>
        )}
      </UserSelectionConfirmModal>

      <EnlargedPhotoModal
        user={enlargedPhotoUser}
        onClose={handleCloseEnlargedPhoto}
      />
      </>)}
    </div>
  );
}
